import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layers, TrendingUp } from "lucide-react";
import { money } from "../lib/api";

// "Bundle & Save" bulk-order meter: shows a live savings counter and a tier ladder.
export const BundleSaveMeter = ({ count, subtotal, tiers = [], stackPercent = 0, onNavigate }) => {
  if (!tiers.length) return null;

  const maxTier = tiers[tiers.length - 1];
  const maxThreshold = maxTier.min_items;
  const maxPercent = maxTier.percent;
  const savings = subtotal * (stackPercent / 100);
  const fill = Math.min(100, (count / maxThreshold) * 100);

  const upcoming = tiers.find((t) => count < t.min_items);
  const message = upcoming
    ? `Add ${upcoming.min_items - count} more item${upcoming.min_items - count === 1 ? "" : "s"} to unlock ${upcoming.percent}% off`
    : `Maximum bulk saving unlocked — ${maxPercent}% off`;

  return (
    <div className="border border-[#27272A] bg-ink-800 p-3 clip-corner" data-testid="bundle-save-meter">
      {/* header + live savings counter */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Layers size={15} className="text-accent" />
          <p className="font-display font-extrabold text-sm tracking-tight leading-none">BUNDLE &amp; SAVE</p>
        </div>
        <div className="text-right">
          {stackPercent > 0 ? (
            <>
              <motion.p
                key={savings}
                initial={{ scale: 0.85, opacity: 0.5 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 18 }}
                className="font-display font-black text-2xl leading-none text-accent"
                data-testid="bulk-savings-amount"
              >
                −{money(savings)}
              </motion.p>
              <p className="font-mono-tech text-[9px] uppercase tracking-widest text-zinc-500 mt-1">
                you save · {stackPercent}% off
              </p>
            </>
          ) : (
            <>
              <p className="font-display font-black text-2xl leading-none text-zinc-300">
                Up to {maxPercent}%
              </p>
              <p className="font-mono-tech text-[9px] uppercase tracking-widest text-zinc-500 mt-1">
                bulk discount
              </p>
            </>
          )}
        </div>
      </div>

      {/* ladder */}
      <div className="relative mt-2 mb-3 mx-1">
        <div className="h-1.5 bg-ink-700 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-accent rounded-full"
            animate={{ width: `${fill}%` }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          />
        </div>
        {tiers.map((t) => {
          const pos = Math.min(100, (t.min_items / maxThreshold) * 100);
          const reached = count >= t.min_items;
          return (
            <div
              key={t.min_items}
              className="absolute -translate-x-1/2 flex flex-col items-center"
              style={{ left: `${pos}%`, top: "-5px" }}
            >
              <span
                className={`h-3.5 w-3.5 rounded-full border-2 transition-colors ${
                  reached ? "bg-accent border-accent" : "bg-ink-900 border-[#3f3f46]"
                }`}
              />
              <span
                className={`font-mono-tech text-[10px] font-bold mt-1 ${
                  reached ? "text-accent" : "text-zinc-500"
                }`}
              >
                {t.percent}%
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-1 border-t border-[#27272A]">
        <span className="flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-widest text-zinc-400">
          <TrendingUp size={12} className="text-accent" /> {message}
        </span>
        <Link
          to="/build"
          onClick={onNavigate}
          className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 hover:text-accent transition-colors shrink-0 ml-3"
        >
          Build →
        </Link>
      </div>
    </div>
  );
};
